import { GameSchema } from '@/lib/types';
import { styled } from '@mui/material/styles';
import { useMemo } from 'react';
import Rtp from '@/components/Rtp/Rtp';
import { getFirstGameByRTP } from '@/lib/game';

interface GameRtpListProps {
  games: GameSchema[];
  onClick?: (studioGameId: string, rtp: number) => void;
  onRtpSelect?: (rtp: number) => void;
  currentRtp?: number;
}

const GameRtpList = ({
  games,
  onClick,
  onRtpSelect,
  currentRtp,
}: GameRtpListProps) => {
  const defaultGame = useMemo(() => {
    if (currentRtp != null) {
      const gameWithCurrentRtp = games.find(
        (g) => g.rtp === currentRtp,
      );
      if (gameWithCurrentRtp) {
        return gameWithCurrentRtp;
      }
    }
    return getFirstGameByRTP(games) || games[0];
  }, [games, currentRtp]);

  const rtps = useMemo(() => {
    const uniqueRtps = Array.from(
      new Set(
        games.map((game) => game.rtp).filter((rtp) => rtp != null),
      ),
    ).sort((a, b) => (a || 0) - (b || 0));
    return uniqueRtps;
  }, [games]);

  const handleRtpClick = (e: React.MouseEvent, rtp: number) => {
    e.stopPropagation();
    if (onRtpSelect) {
      onRtpSelect(rtp);
    } else if (onClick) {
      const gameWithRtp = games.find((g) => g.rtp === rtp);
      if (gameWithRtp) {
        onClick(gameWithRtp.studio_game_id, rtp);
      }
    }
  };

  if (rtps.length === 0) {
    return null;
  }

  return (
    <RtpList>
      {rtps.map((rtp) => (
        <RtpBox
          key={rtp}
          onClick={(e) => handleRtpClick(e, rtp)}
          $isDefault={rtp === defaultGame.rtp}
        >
          <Rtp value={rtp} />
        </RtpBox>
      ))}
    </RtpList>
  );
};

const RtpList = styled('div')(() => ({
  display: 'flex',
  gap: 8,
  flexWrap: 'wrap',
  flex: 1,
}));

const RtpBox = styled('div')<{ $isDefault: boolean }>(
  ({ theme, $isDefault }) => ({
    padding: '4px 8px',
    borderRadius: theme.radii.sm || '8px',
    border: `1px solid ${
      $isDefault
        ? theme.palette.primary.main
        : theme.palette.textPrimaryWhite
    }`,
    background: $isDefault
      ? theme.palette.primary.main
      : 'rgba(255, 255, 255, 0.1)',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '16px',
    color: theme.palette.textPrimaryWhite,
    transition: 'all 0.2s ease-in-out',

    '&:hover': {
      background: $isDefault
        ? theme.palette.primary.dark
        : 'rgba(255, 255, 255, 0.2)',
      borderColor: $isDefault
        ? theme.palette.primary.dark
        : theme.palette.textPrimaryWhite,
    },
  }),
);

export default GameRtpList;
